import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as metricsApi from "@/api/metrics.api";
import type { Metric } from "@/types";

interface MetricsState {
  items: Metric[];
  loading: boolean;
  error: string | null;
}

const initialState: MetricsState = {
  items: [],
  loading: false,
  error: null,
};

export const fetchMetrics = createAsyncThunk("metrics/fetchAll", async () =>
  metricsApi.getMetrics(),
);

export const createMetric = createAsyncThunk(
  "metrics/create",
  async (data: {
    name: string;
    shortName?: string;
    displayName?: string;
    slug?: string;
    description?: string;
  }) => metricsApi.createMetric(data),
);

export const updateMetric = createAsyncThunk(
  "metrics/update",
  async ({ id, data }: { id: string; data: Partial<Metric> }) =>
    metricsApi.updateMetric(id, data),
);

export const deleteMetric = createAsyncThunk(
  "metrics/delete",
  async (id: string) => {
    await metricsApi.deleteMetric(id);
    return id;
  },
);

const metricsSlice = createSlice({
  name: "metrics",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMetrics.fulfilled, (state, action) => {
        state.items = action.payload;
        state.loading = false;
      })
      .addCase(fetchMetrics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch metrics";
      })
      .addCase(createMetric.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
      })
      .addCase(updateMetric.fulfilled, (state, action) => {
        const idx = state.items.findIndex((m) => m.id === action.payload.id);
        if (idx >= 0) state.items[idx] = action.payload;
      })
      .addCase(deleteMetric.fulfilled, (state, action) => {
        state.items = state.items.filter((m) => m.id !== action.payload);
      });
  },
});

export default metricsSlice.reducer;
